/**
 * Eksportpakker for «Last ned»: få, faglige valg i stedet for én rad per lag. Hver pakke gir ÉN fil med flere lag
 * (GeoPackage), eller én samlet fil for GeoJSON/CSV. Enkeltlagene ligger fortsatt under «Avansert» i Full visning.
 *
 * Rene funksjoner (menymodell, sammenslått fallrisiko, filnavn) – testes i tests/unit/exportPresets.test.ts.
 */

import type { Feature } from '../layers/geojson';
import { featureStyle, flag, writtenFlag } from '../layers/featureStyle';
import { fallFilterSlug, fallFilterText, fallVisible, isFallRiskLayer } from '../layers/fallRisk';

export type PresetId = 'linjeanlegg' | 'vegetasjon' | 'fallrisiko' | 'stasjoner' | 'komplett';

/** Ett lag i eksportfilen: navn i filen (norsk snake_case) og kildelaget/-lagene i prosjektet. */
export interface PresetLayer {
  name: string;
  source: string;
  /** linjeobjekt: bare klassifiserte/validerte (usikre bare når «Vis usikre mastkandidater» er på), med eksportstatus */
  kind?: 'line-object' | 'fall';
}
export interface ExportPreset { id: PresetId; title: string; slug: string; note: string; layers: PresetLayer[] }

/** Fallrisiko fra toppen: tre lag i prosjektet (bue, del over linja, kontaktpunkt) → ett objekt per tre. */
export const FALL_SOURCES = { arcs: 'veg_fall_top_arcs', contacts: 'veg_fall_contacts', overshoot: 'veg_fall_overshoot' } as const;
/** Eldre kjøringer uten fallrisiko fra toppen */
export const FALL_FALLBACK = 'veg_fall_lines';
/** Motorens ETT fallrisiko-lag (LineStringZ per tre, norske felt) – eksporteres direkte når det finnes */
export const FALL_DIRECT = 'fallrisiko';

const LINE_LAYERS: PresetLayer[] = [
  { name: 'stolper', source: 'support_poles', kind: 'line-object' },
  { name: 'stolpeakser', source: 'support_pole_axes', kind: 'line-object' },
  { name: 'traverser', source: 'support_traverses', kind: 'line-object' },
  { name: 'barduner', source: 'support_guys', kind: 'line-object' },
  { name: 'isolatorer', source: 'insulators', kind: 'line-object' },
  { name: 'ledninger', source: 'conductor_wires', kind: 'line-object' },
  { name: 'spenn', source: 'conductor_spans', kind: 'line-object' },
];
const FALL_LAYER: PresetLayer = { name: 'fallrisiko', source: FALL_SOURCES.arcs, kind: 'fall' };
const VEG_LAYERS: PresetLayer[] = [
  { name: 'minsteavstand_brudd', source: 'veg_clearance_violations' },
  { name: 'minsteavstand_linjer', source: 'veg_clearance_lines' },
  FALL_LAYER,
  { name: 'faretraer', source: 'veg_danger_trees' },
];

/** Stasjonslag (når prosjektet har dem): lag-ID med station/substation/stasjon. */
export const isStationLayer = (id: string) => /(^|_)(sub)?station|stasjon/i.test(id);

export const PRESETS: ExportPreset[] = [
  { id: 'linjeanlegg', title: 'Linjeanlegg', slug: 'linjeanlegg', layers: LINE_LAYERS,
    note: 'Stolper, stolpeakser, traverser, barduner, isolatorer, ledninger og spenn. Bare klassifiserte objekter, med mindre «Vis usikre mastkandidater» er på; kolonnen eksportstatus viser hva som er hva.' },
  { id: 'vegetasjon', title: 'Vegetasjon og rydding', slug: 'vegetasjon-og-rydding', layers: VEG_LAYERS,
    note: 'Minsteavstand (brudd og avstandslinjer), fallrisiko (ett objekt per tre) og faretrær. UNCALIBRATED – ikke forskriftsmessig.' },
  { id: 'fallrisiko', title: 'Fallrisiko', slug: 'fallrisiko', layers: [FALL_LAYER],
    note: 'Ett objekt per tre: buen fra tretoppen til der treet lander, med kontaktpunkt, landingspunkt, lengde over linja og last-indeks som attributter.' },
  { id: 'stasjoner', title: 'Stasjoner', slug: 'stasjoner', layers: [], note: 'Stasjonslagene i prosjektet.' },
  { id: 'komplett', title: 'Alt (komplett pakke)', slug: 'komplett', layers: [], note: 'Linjeanlegg, vegetasjon og rydding (med fallrisiko) og stasjoner i én fil.' },
];
export const presetById = (id: string) => PRESETS.find((p) => p.id === id);

const snake = (s: string) => asciiSafe(s).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'lag';
const stationLayers = (available: Iterable<string>): PresetLayer[] =>
  [...available].filter(isStationLayer).sort().map((id) => ({ name: snake(id.replace(/^(sub)?stations?_?/i, 'stasjon_')), source: id }));

/**
 * Lagene en pakke består av i dette prosjektet (bare de som finnes). Fallrisiko: motorens lag `fallrisiko` eksporteres
 * direkte; ellers slås de tre eldre lagene sammen (mergeFallRisk), og aller sist brukes eldre fall-linjer.
 */
export function presetLayers(preset: ExportPreset, available: Set<string>): PresetLayer[] {
  const parts = preset.id === 'komplett' ? [...LINE_LAYERS, ...VEG_LAYERS, ...stationLayers(available)]
    : preset.id === 'stasjoner' ? stationLayers(available) : preset.layers;
  const out: PresetLayer[] = [];
  for (const p of parts) {
    if (p.kind === 'fall') {
      if (available.has(FALL_DIRECT)) out.push({ name: p.name, source: FALL_DIRECT });
      else if (available.has(FALL_SOURCES.arcs)) out.push(p);
      else if (available.has(FALL_FALLBACK)) out.push({ name: p.name, source: FALL_FALLBACK });
    } else if (available.has(p.source)) out.push(p);
  }
  return out.filter((p, i) => out.findIndex((q) => q.name === p.name) === i);
}

/** Kildelagene som må være lastet for å bygge pakken. */
export function presetSources(preset: ExportPreset, available: Set<string>): string[] {
  return [...new Set(presetLayers(preset, available).flatMap((p) => p.kind === 'fall'
    ? Object.values(FALL_SOURCES).filter((id) => available.has(id)) : [p.source]))];
}

export interface MenuItem { value: string; title: string; note?: string }
/**
 * Menyen: pakkene som har innhold i prosjektet (Stasjoner bare med stasjonslag), og – utenfor Resultat-visningen –
 * «Avansert»: alle synlige lag og hvert lastet enkeltlag (også støyklynger og de tre fallrisikolagene).
 */
export function exportMenu(available: Set<string>, resultMode: boolean, loaded: { id: string; title: string }[] = []): { presets: MenuItem[]; advanced: MenuItem[] } {
  const presets = PRESETS.filter((p) => presetLayers(p, available).length).map((p) => ({ value: `preset:${p.id}`, title: p.title, note: p.note }));
  const advanced = resultMode && presets.length ? [] : [{ value: 'visible', title: 'Alle lastede, synlige vektorlag' }, ...loaded.map((l) => ({ value: l.id, title: l.title }))];
  return { presets, advanced };
}

// ------------------------------------------------------------------ filtrering

export type ExportStatus = 'klassifisert' | 'usikker' | 'forkastet' | 'ukjent';
/** Klassifisert i punktskyen (flagg), ellers status: VALIDATED/DELIVERED → klassifisert, REJECTED → forkastet. */
export function exportStatus(props: Record<string, unknown>): ExportStatus {
  const st = typeof props.status === 'string' ? props.status.trim().toUpperCase() : '';
  if (st === 'REJECTED') return 'forkastet';
  const w = writtenFlag(props);
  if (w !== undefined) return w ? 'klassifisert' : 'usikker';
  if (st === 'VALIDATED' || st === 'DELIVERED') return 'klassifisert';
  return st ? 'usikker' : 'ukjent';
}

const r3 = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? Math.round(v * 1000) / 1000 : null);
const xyz = (prefix: string, p: number[] | undefined): Record<string, number | null> =>
  ({ [`${prefix}_x`]: r3(p?.[0]), [`${prefix}_y`]: r3(p?.[1]), [`${prefix}_z`]: r3(p?.[2]) });
const fallKey = (p: Record<string, unknown>) => `${String(p.tree_id ?? '')}|${String(p.wire_id ?? '')}`;

/**
 * Slår sammen de tre fallrisikolagene til ett objekt per tre: geometri = toppens bue (tretopp → der treet lander),
 * med kontaktpunkt, landingspunkt og tretopp som koordinatattributter. Lengde over linja, andel av stammen, last-indeks,
 * manglende avstand osv. kommer fra buen. Bare trær som treffer linja og oppfyller fallrisiko-filteret tas med.
 */
export function mergeFallRisk(arcs: Feature[], contacts: Feature[] = [], overshoot: Feature[] = []): Feature[] {
  const byKey = (fs: Feature[]) => new Map(fs.map((f) => [fallKey(f.props), f]));
  const c = byKey(contacts), o = byKey(overshoot);
  return arcs.filter((a) => fallVisible(a.props)).map((a) => {
    const line = a.parts[0] ?? [];
    const contact = c.get(fallKey(a.props))?.parts[0]?.[0];
    const over = o.get(fallKey(a.props))?.parts[0];
    const geo = !!flag(a.props.hits_geometric);
    return { ...a, props: {
      ...a.props,
      vurdering: geo ? 'treffer linja ved fall' : 'innenfor minsteavstanden, treffer ikke linja',
      ...xyz('top', line[0]),
      ...xyz('contact', contact),
      ...xyz('landing', over?.[over.length - 1] ?? line[line.length - 1]),
      har_del_over_linja: over ? 1 : 0,
    } };
  });
}

export interface BuiltLayer { name: string; source: string; features: Feature[] }
/** Bygg lagene i en pakke fra lastede kildelag (`get` gir features etter lagets attributtfilter, eller undefined). */
export function buildPresetLayers(preset: ExportPreset, available: Set<string>, get: (id: string) => Feature[] | undefined,
  opts: { includeUncertain: boolean }): BuiltLayer[] {
  return presetLayers(preset, available).map((p) => {
    const src = get(p.source) ?? [];
    let features: Feature[];
    if (p.kind === 'fall') features = mergeFallRisk(src, get(FALL_SOURCES.contacts), get(FALL_SOURCES.overshoot));
    else if (p.kind === 'line-object') {
      features = src.flatMap((f) => {
        const st = exportStatus(f.props);
        return st === 'forkastet' || (st === 'usikker' && !opts.includeUncertain) ? [] : [{ ...f, props: { ...f.props, eksportstatus: st } }];
      });
    } else features = src.filter((f) => !featureStyle(p.source, f.props)?.hidden);
    return { name: p.name, source: p.source, features };
  });
}

/** Norsk lagnavn for et enkeltlag i «Avansert» (filnavn), ellers lag-ID-en. */
export function layerExportName(id: string): string {
  const hit = [...LINE_LAYERS, ...VEG_LAYERS].find((p) => p.source === id && p.kind !== 'fall');
  if (hit) return hit.name;
  return ({ veg_fall_top_arcs: 'fallrisiko_buer', veg_fall_overshoot: 'fallrisiko_over_linja', veg_fall_contacts: 'fallrisiko_kontaktpunkt',
    noise_clusters: 'stoyklynger', veg_fall_lines: 'fallrisiko_linjer' } as Record<string, string>)[id] ?? snake(id);
}

// ------------------------------------------------------------------ filnavn

/** Pakker/lag som inneholder fallrisiko-trær (eksporten bruker fallrisiko-filteret). */
export const hasFallRisk = (layer: string): boolean => /^preset:(fallrisiko|vegetasjon|komplett)$/.test(layer) || isFallRiskLayer(layer) || layer === 'visible';
/** Filnavn-ledd og rapportlinje som forteller hvilket fallrisiko-filter som gjaldt, eller '' når eksporten ikke har fallrisiko. */
export const fallExportSlug = (layer: string): string => (hasFallRisk(layer) ? `-${fallFilterSlug()}` : '');
export const fallExportNote = (layer: string): string => (hasFallRisk(layer) ? `Fallrisiko-filter: ${fallFilterText()}.` : '');

/** æøå → ae/o/a, andre aksenter fjernes. */
export function asciiSafe(s: string): string {
  return s.replace(/æ/g, 'ae').replace(/Æ/g, 'Ae').replace(/ø/g, 'o').replace(/Ø/g, 'O').replace(/å/g, 'a').replace(/Å/g, 'A')
    .normalize('NFD').replace(/[̀-ͯ]/g, '');
}
const localDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const token = (s: string) => asciiSafe(s).replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^[-_]+|[-_]+$/g, '');

/**
 * `<Prosjekt>_<kjøring>_<innhold>_<YYYY-MM-DD>.<ext>`, f.eks. `Glitre_v2_linjeanlegg_2026-10-07.gpkg`. Prosjektet er
 * første ledd av prosjektnavnet («Glitre – klassifisering (lokal)» → Glitre); kjøringsnavn som allerede begynner med
 * prosjektet («Glitre_v2») brukes som det er. Innholdet skrives med små bokstaver og bindestrek.
 */
export function exportFileName(o: { project: string; run?: string | null; content: string; ext: string; date?: Date }): string {
  const project = token(asciiSafe(o.project).split(/\s+[–—-]\s+|\s*\(/)[0].trim()) || 'prosjekt';
  const run = o.run ? token(o.run) : '';
  const base = !run ? project : run.toLowerCase().startsWith(project.toLowerCase()) ? run : `${project}_${run}`;
  const content = asciiSafe(o.content).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'eksport';
  return `${base}_${content}_${localDate(o.date ?? new Date())}.${o.ext.replace(/^\./, '')}`;
}
